import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

/* Very simple decimal library backed by BigInt — supports +, −, ×, ÷ with
   fixed precision, plus integer-only exponent, mod, sqrt, factorial, gcd, lcm. */
function toScaled(str, prec) {
  str = String(str).trim();
  if (!str) return null;
  const neg = str.startsWith('-'); if (neg) str = str.slice(1);
  if (str.startsWith('+')) str = str.slice(1);
  let [int, frac = ''] = str.split('.');
  if (frac.length > prec) frac = frac.slice(0, prec);
  while (frac.length < prec) frac += '0';
  try {
    const v = BigInt((int || '0') + frac);
    return neg ? -v : v;
  } catch { return null; }
}
function fromScaled(v, prec) {
  const neg = v < 0n; if (neg) v = -v;
  const s = v.toString().padStart(prec + 1, '0');
  const int = s.slice(0, s.length - prec) || '0';
  const frac = prec ? s.slice(s.length - prec) : '';
  const trimmed = frac.replace(/0+$/, '');
  return (neg ? '-' : '') + int + (trimmed ? '.' + trimmed : '');
}
function bigSqrt(n) {
  if (n < 0n) throw new Error('sqrt of negative');
  if (n < 2n) return n;
  let x = n, y = (x + 1n) / 2n;
  while (y < x) { x = y; y = (x + n / x) / 2n; }
  return x;
}
function bigAbs(n) { return n < 0n ? -n : n; }

export default function BigNumberCalc() {
  const [a, setA] = useState('12345678901234567890.123456789');
  const [b, setB] = useState('98765432109876543210.987654321');
  const [op, setOp] = useState('+');
  const [prec, setPrec] = useState(10);
  const [showThousands, setShowThousands] = useState(false);
  const [showSciNotation, setShowSciNotation] = useState(false);
  const [expMode, setExpMode] = useState('auto');

  const result = useMemo(() => {
    try {
      const p = Math.max(0, Math.min(200, Number(prec) || 0));
      const scale = 10n ** BigInt(p);
      if (op === 'factorial') {
        const n = BigInt(a.replace(/[^-\d]/g, '')); if (n < 0n) return 'n must be ≥ 0';
        if (n > 5000n) return 'Too large — max 5000 for factorial.';
        let r = 1n; for (let i = 2n; i <= n; i++) r *= i;
        return r.toString();
      }
      if (op === 'gcd' || op === 'lcm') {
        const A = BigInt(a.replace(/[^-\d]/g, '')); const B = BigInt(b.replace(/[^-\d]/g, ''));
        const gcd = (x, y) => { x = bigAbs(x); y = bigAbs(y); while (y) [x, y] = [y, x % y]; return x; };
        const g = gcd(A, B);
        if (op === 'gcd') return g.toString();
        return g === 0n ? '0' : ((A * B) / g < 0n ? -(A * B) / g : (A * B) / g).toString();
      }
      if (op === 'sqrt') {
        const A = toScaled(a, p); if (A === null) return 'Invalid';
        if (A < 0n) return 'sqrt of negative';
        const scaled = A * scale;
        return fromScaled(bigSqrt(scaled), p);
      }
      const A = toScaled(a, p); const B = toScaled(b, p);
      if (A === null || B === null) return 'Invalid';
      if (op === '+') return fromScaled(A + B, p);
      if (op === '-') return fromScaled(A - B, p);
      if (op === '*' || op === '×') return fromScaled((A * B) / scale, p);
      if (op === '/' || op === '÷') { if (B === 0n) return '∞ (divide by zero)'; return fromScaled((A * scale) / B, p); }
      if (op === '^') {
        const eInt = BigInt(b.replace(/[^-\d]/g, ''));
        if (eInt < 0n) return 'B must be ≥ 0 for exponentiation.';
        if (eInt > 5000n) return 'Exponent too large (max 5000).';
        let r = scale, base = A;
        for (let i = 0n; i < eInt; i++) { r = (r * base) / scale; if (r.toString().length > 2000) return 'Result exceeds 2000 digits'; }
        return fromScaled(r, p);
      }
      if (op === 'mod') { if (B === 0n) return '∞'; return fromScaled(A % B, p); }
      return '';
    } catch (e) { return 'Invalid: ' + e.message; }
  }, [a, b, op, prec]);

  const displayed = useMemo(() => {
    let s = String(result);
    if (showThousands && /^-?\d+(\.\d+)?$/.test(s)) {
      const [i, f] = s.split('.');
      const withCommas = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      s = withCommas + (f ? '.' + f : '');
    }
    if (showSciNotation && /^-?\d+(\.\d+)?$/.test(s)) {
      const num = Number(s);
      if (isFinite(num) && Math.abs(num) > 0) {
        s = num.toExponential(Math.min(20, Number(prec) || 6));
      }
    }
    return s;
  }, [result, showThousands, showSciNotation, prec]);

  return (
    <CalcShell look="cnet" title="Big Number Calculator" subtitle="Arithmetic on arbitrarily large numbers with configurable decimal precision.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="field"><label>Number A</label><textarea rows={3} value={a} onChange={e => setA(e.target.value)} data-testid="bn-a" className="w-full font-mono text-sm p-2 border border-slate-300 rounded" /></div>
          <div className="field"><label>Operation</label>
            <select value={op} onChange={e => setOp(e.target.value)} data-testid="bn-op">
              <option value="+">Addition (A + B)</option>
              <option value="-">Subtraction (A − B)</option>
              <option value="*">Multiplication (A × B)</option>
              <option value="/">Division (A ÷ B)</option>
              <option value="^">Exponentiation (A^B, integer B)</option>
              <option value="mod">Modulo (A mod B)</option>
              <option value="gcd">GCD(A, B)</option>
              <option value="lcm">LCM(A, B)</option>
              <option value="sqrt">Square Root (√A)</option>
              <option value="factorial">Factorial (A!)</option>
            </select>
          </div>
          {op !== 'factorial' && op !== 'sqrt' && (
            <div className="field"><label>Number B</label><textarea rows={3} value={b} onChange={e => setB(e.target.value)} data-testid="bn-b" className="w-full font-mono text-sm p-2 border border-slate-300 rounded" /></div>
          )}
          <div className="field"><label>Precision (decimal places)</label>
            <input type="number" min="0" max="200" value={prec} onChange={e => setPrec(e.target.value)} data-testid="bn-prec" />
            <div className="text-xs text-slate-500 mt-1">0-200 digits after the decimal point.</div>
          </div>
          <label className="flex items-center gap-2 text-sm py-1"><input type="checkbox" checked={showThousands} onChange={e => setShowThousands(e.target.checked)} data-testid="bn-thousands" /> Show thousands separators</label>
          <label className="flex items-center gap-2 text-sm py-1"><input type="checkbox" checked={showSciNotation} onChange={e => setShowSciNotation(e.target.checked)} data-testid="bn-sci" /> Display in scientific notation</label>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Result</div>
            <div className="text-lg font-mono break-all bg-white/50 p-2 rounded mt-1" data-testid="bn-result">{displayed}</div>
            <div className="text-xs text-slate-500 mt-2">Precision: {prec} digits · Length: {String(result).length} chars</div>
          </div>
        </div>
      </div>
      <StepsPanel steps={[
        `Custom big-decimal library backed by BigInt with fixed-scale arithmetic.`,
        `Precision: ${prec} decimal places · Operation: ${op}`,
      ]} />
    </CalcShell>
  );
}
